import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, FileText, Scale, Plus, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LibrariesPage = () => {
  const [standardTexts, setStandardTexts] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [legalBases, setLegalBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('texts');
  
  // Dialog states
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [reqDialogOpen, setReqDialogOpen] = useState(false);
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [textsRes, reqsRes, legalRes] = await Promise.all([
        axios.get(`${API_URL}/standard-texts`, { withCredentials: true }),
        axios.get(`${API_URL}/functional-requirements`, { withCredentials: true }),
        axios.get(`${API_URL}/legal-bases`, { withCredentials: true })
      ]);
      setStandardTexts(textsRes.data);
      setRequirements(reqsRes.data);
      setLegalBases(legalRes.data);
    } catch (error) {
      toast.error('Erro ao carregar bibliotecas');
    } finally {
      setLoading(false);
    }
  };

  const categories = {
    texts: [
      'Justificativa Legal', 'Objeto da Proposta', 'Garantia', 
      'SLA', 'Forma de Pagamento', 'Penalidades'
    ],
    requirements: [
      'Segurança da Informação', 'Gestão Patrimonial', 'Compras e Licitações',
      'Contratos', 'Almoxarifado', 'Gestão de Frotas'
    ]
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Texto copiado para área de transferência');
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="libraries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Bibliotecas de Conhecimento</h1>
          <p className="text-[#6E6E73]">Textos padrão, requisitos e bases legais reutilizáveis</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="texts" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Textos Padrão ({standardTexts.length})
          </TabsTrigger>
          <TabsTrigger value="requirements" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Requisitos ({requirements.length})
          </TabsTrigger>
          <TabsTrigger value="legal" className="flex items-center gap-2">
            <Scale className="w-4 h-4" />
            Bases Legais ({legalBases.length})
          </TabsTrigger>
        </TabsList>

        {/* TEXTOS PADRÃO */}
        <TabsContent value="texts" className="space-y-4">
          <div className="bg-white border border-[#E5E5EA] rounded-xl p-6">
            <div className="space-y-4">
              {categories.texts.map((category) => {
                const items = standardTexts.filter(t => t.category === category);
                if (items.length === 0) return null;
                
                return (
                  <div key={category} className="space-y-2">
                    <h3 className="font-semibold text-[#111111] border-b border-[#E5E5EA] pb-2">
                      {category} ({items.length})
                    </h3>
                    <div className="grid gap-3">
                      {items.map((text) => (
                        <div
                          key={text.text_id}
                          className="border border-[#E5E5EA] rounded-lg p-4 hover:shadow-md hover:border-[#AF2628] transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-[#111111] mb-2">{text.title}</h4>
                              <p className="text-sm text-[#6E6E73] whitespace-pre-wrap">{text.content}</p>
                              {text.tags && text.tags.length > 0 && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                  {text.tags.map((tag, idx) => (
                                    <span
                                      key={`${tag}-${idx}`}
                                      className="text-xs bg-[#F7F7F8] px-2 py-1 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(text.content)}
                              className="ml-4"
                            >
                              Copiar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* REQUISITOS FUNCIONAIS */}
        <TabsContent value="requirements" className="space-y-4">
          <div className="bg-white border border-[#E5E5EA] rounded-xl p-6">
            <div className="space-y-4">
              {categories.requirements.map((category) => {
                const items = requirements.filter(r => r.categoria === category);
                if (items.length === 0) return null;
                
                return (
                  <div key={category} className="space-y-2">
                    <h3 className="font-semibold text-[#111111] border-b border-[#E5E5EA] pb-2">
                      {category} ({items.length})
                    </h3>
                    <div className="space-y-2">
                      {items.map((req) => (
                        <div
                          key={req.requirement_id}
                          className="border border-[#E5E5EA] rounded-lg p-3 hover:bg-[#FFF5F5] transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <span className="font-mono text-sm font-medium text-[#AF2628] bg-[#FFF1F0] px-2 py-1 rounded">
                              {req.codigo}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm text-[#111111]">{req.descricao}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              req.tipo === 'OBRIGATÓRIO' 
                                ? 'bg-[#FFF1F0] text-[#E11F20]' 
                                : 'bg-[#F0F9FF] text-[#0EA5E9]'
                            }`}>
                              {req.tipo}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* BASES LEGAIS */}
        <TabsContent value="legal" className="space-y-4">
          <div className="bg-white border border-[#E5E5EA] rounded-xl p-6">
            <div className="space-y-3">
              {legalBases.map((legal) => (
                <div
                  key={legal.legal_id}
                  className="border border-[#E5E5EA] rounded-lg p-4 hover:shadow-md hover:border-[#AF2628] transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F0FDF4] w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Scale className="w-6 h-6 text-[#22C55E]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-[#111111]">{legal.tipo} nº {legal.numero}</h4>
                          <p className="text-sm font-medium text-[#6E6E73]">{legal.titulo}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(`${legal.tipo} nº ${legal.numero} - ${legal.titulo}\n\n${legal.resumo}`)}
                        >
                          Copiar
                        </Button>
                      </div>
                      <p className="text-sm text-[#6E6E73] mb-3">{legal.resumo}</p>
                      <div className="flex gap-2 flex-wrap">
                        {legal.aplicavel_a && legal.aplicavel_a.map((tipo, idx) => (
                          <span
                            key={`${tipo}-${idx}`}
                            className="text-xs bg-[#F7F7F8] px-2 py-1 rounded"
                          >
                            {tipo}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="bg-[#FFF1F0] border border-[#FFCCC7] rounded-xl p-4">
        <p className="text-sm text-[#6E6E73]">
          <strong className="text-[#E11F20]">Dica:</strong> Use o botão "Copiar" para adicionar textos aos seus documentos.
          Estes textos são padronizados e aprovados para uso em propostas, TRs e ETPs.
        </p>
      </div>
    </div>
  );
};

export default LibrariesPage;
